import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { User, Search, Paperclip } from "lucide-react";
import { Dialog, DialogContent } from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import {
  useNscAddConsumers,
  useTerritoryPremiseStats,
  useUpdateAssignedServiceAddress,
} from "@features/onboarding/utility-setup/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useDebounce } from "@shared/hooks/useDebounce";
import { useToast } from "@shared/ui/use-toast";

export interface CandidateConsumer {
  id: string;
  avatar: string;
  name: string;
  accountNo: string;
  email: string;
  phone: string;
  plan: string;
  remotePlanId?: number;
  services: string[];
  status: string;
}

interface SelectConsumerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onAttachConsumer: (consumer: CandidateConsumer) => void;
}

export const SelectConsumerModal: React.FC<SelectConsumerModalProps> = ({
  isOpen,
  onClose,
  onBack,
  onAttachConsumer,
}) => {
  const { code } = useParams<{ code: string }>();
  const decodedUnitCode = useMemo(() => decodeURIComponent(code || ""), [code]);
  const { data: statsData } = useTerritoryPremiseStats(decodedUnitCode);
  const rawStatsData: any = statsData;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedConsumer, setSelectedConsumer] =
    useState<CandidateConsumer | null>(null);
  const remoteUtilityId = useRemoteUtilityId();
  const debouncedSearch = useDebounce(searchQuery, 300);
  const { toast } = useToast();

  const updateAssignedServiceAddressMutation =
    useUpdateAssignedServiceAddress();

  const { data: nscAddData, isLoading } = useNscAddConsumers({
    remoteUtilityId,
    searchData: debouncedSearch,
    enabled: isOpen,
  });

  const candidateConsumers: CandidateConsumer[] = useMemo(() => {
    const rawList = nscAddData?.results;

    if (Array.isArray(rawList) && rawList.length > 0) {
      return rawList.map((item: any) => {
        const firstName = item.firstName || "N/A";
        const lastName = item.lastName || "N/A";
        const fullName =
          `${firstName} ${lastName}`.trim() || item.consumerNo || "N/A";

        const initials =
          fullName
            .split(" ")
            .map((n: string) => n[0])
            .filter(Boolean)
            .join("")
            .toUpperCase()
            .slice(0, 2) || "NA";

        const rawServices = item.utilityServices || [];

        const services: string[] = Array.isArray(rawServices)
          ? rawServices
          : [];

        const planVal = item.planName || "N/A";

        return {
          id: item.id ?? "N/A",
          avatar: initials,
          name: fullName,
          accountNo: item.consumerNo || "N/A",
          email: item.email || "N/A",
          phone: item.contactNumber || "N/A",
          plan: planVal ? String(planVal) : "N/A",
          remotePlanId: item.remotePlanId
            ? Number(item.remotePlanId)
            : undefined,
          services,
          status: item.statusDisplay || "N/A",
        };
      });
    }

    return [];
  }, [nscAddData]);

  const filteredConsumers = useMemo(() => {
    if (!searchQuery.trim()) return candidateConsumers;
    const q = searchQuery.toLowerCase().trim();
    return candidateConsumers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.accountNo.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.phone.includes(q),
    );
  }, [searchQuery, candidateConsumers]);

  const handleModalClose = () => {
    setSelectedConsumer(null);
    setSearchQuery("");
    onClose();
  };

  const handleAttachConsumerSubmit = () => {
    if (!selectedConsumer) return;

    const basicInfo: any = rawStatsData?.basicInfo || {};
    const hierarchyCode: any = rawStatsData?.hierarchyCode || {};

    const city = hierarchyCode.county || "";
    const zone = hierarchyCode.zone || "";
    const division = hierarchyCode.division || "";
    const area = hierarchyCode.area || "";
    const subArea = hierarchyCode.subArea || "";
    const premise = hierarchyCode.premise || "";
    const unit = basicInfo.code || decodedUnitCode || "";

    const remotePlanId = Number(basicInfo.planId);
    const utilityServices = basicInfo.utilityServices;

    updateAssignedServiceAddressMutation.mutate(
      {
        consumerId: selectedConsumer.id,
        service_address_data: {
          unit,
          address: basicInfo.description || basicInfo.address,
          city,
          zone,
          division,
          area,
          sub_area: subArea,
          premise,
          zipcode: basicInfo.zipcode,
        },
        remote_plan_id: remotePlanId,
        utility_services: utilityServices,
      },
      {
        onSuccess: () => {
          onAttachConsumer(selectedConsumer);
          setSelectedConsumer(null);
          setSearchQuery("");
        },
      },
    );
  };

  const getBadgeStyle = (service: string) => {
    switch (service) {
      case "Water":
        return "bg-blue-50 text-blue-600 border-blue-100";
      case "Hot Water":
        return "bg-rose-50 text-rose-600 border-rose-100";
      case "Broadband":
        return "bg-purple-50 text-purple-600 border-purple-100";
      case "Electricity":
        return "bg-amber-50 text-amber-700 border-amber-100";
      case "Gas":
        return "bg-orange-50 text-orange-600 border-orange-100";
      case "Solar":
        return "bg-teal-50 text-teal-700 border-teal-100";
      case "Waste Water":
        return "bg-emerald-50 text-emerald-700 border-emerald-100";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const selectedConsumerDetails = useMemo(() => {
    if (!selectedConsumer) return [];
    return [
      {
        label: "email",
        value: selectedConsumer.email || "N/A",
      },
      {
        label: "phone",
        value: selectedConsumer.phone || "N/A",
      },
      {
        label: "plan",
        value: selectedConsumer.plan || "N/A",
      },
      {
        label: "utility services",
        value:
          selectedConsumer.services && selectedConsumer.services.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedConsumer.services.map((svc, i) => (
                <span
                  key={i}
                  className={`text-[10px] font-medium px-2 py-0.5 rounded border ${getBadgeStyle(
                    svc,
                  )}`}
                >
                  {svc}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-400 font-normal">N/A</span>
          ),
      },
    ];
  }, [selectedConsumer]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleModalClose()}>
      <DialogContent className="max-w-lg p-6 rounded-2xl bg-white space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
          <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <User className="w-4 h-4" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Update Consumer</h2>
        </div>

        {/* Step 2A: Search & Selection List View */}
        {!selectedConsumer ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Search for a consumer to assign to this unit.
            </p>

            {/* Search Input Box */}
            <div className="relative flex items-center">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name, account, email or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs font-medium border border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg shadow-2xs text-gray-800"
              />
            </div>

            {/* Consumer List */}
            <div className="border border-gray-200/80 rounded-xl max-h-64 overflow-y-auto divide-y divide-gray-100">
              {isLoading ? (
                <div className="p-4 text-center text-xs text-gray-400 font-medium">
                  Loading consumers...
                </div>
              ) : filteredConsumers.length > 0 ? (
                filteredConsumers.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedConsumer(item)}
                    className="p-3.5 flex items-center justify-between cursor-pointer hover:bg-blue-50/40 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-600 font-bold text-xs flex items-center justify-center shrink-0">
                        {item.avatar}
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-gray-900">
                          {item.name}
                        </h4>
                        <p className="text-[11px] text-gray-400">
                          {item.accountNo} · {item.email}
                        </p>
                      </div>
                    </div>

                    <span className="text-xs text-gray-400 font-normal">
                      {item.plan}
                    </span>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-xs text-gray-400 font-medium">
                  No matching consumers found
                </div>
              )}
            </div>

            {/* Step 2A Footer */}
            <div className="flex items-center justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  onBack();
                }}
                className="px-5 py-2 text-xs font-semibold text-gray-700 h-9 rounded-lg cursor-pointer"
              >
                Back
              </Button>
            </div>
          </div>
        ) : (
          /* Step 2B: Selected Consumer Detail & Attach View */
          <div className="space-y-4 pt-1">
            <p className="text-xs font-semibold text-gray-500">
              selected consumer
            </p>

            <div className="border border-blue-200 bg-blue-50/20 rounded-xl p-4 space-y-4">
              {/* Header Info Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 font-bold text-sm flex items-center justify-center shrink-0">
                    {selectedConsumer.avatar}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">
                      {selectedConsumer.name}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {selectedConsumer.accountNo}
                    </p>
                  </div>
                </div>

                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100 inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Active
                </span>
              </div>

              {/* 2x2 Details Grid */}
              <div className="grid grid-cols-2 gap-4 pt-1">
                {selectedConsumerDetails.map((detail, index) => (
                  <ValueLabel
                    key={index}
                    label={detail.label}
                    value={detail.value}
                  />
                ))}
              </div>
            </div>

            {/* Step 2B Footer */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setSelectedConsumer(null)}
                className="px-5 py-2 text-xs font-semibold text-gray-700 h-9 rounded-lg cursor-pointer"
              >
                Back
              </Button>

              <Button
                onClick={handleAttachConsumerSubmit}
                isLoading={updateAssignedServiceAddressMutation.isPending}
                variant="default"
              >
                <Paperclip className="w-4 h-4" />
                Attach Consumer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SelectConsumerModal;
